import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Shared user avatar.
 *
 * Renders one of two things:
 *   1. The user's uploaded `avatarUrl` as an `<img>` cover
 *   2. The user's initials over the brand ocean→cyan gradient
 *
 * Resilience: if the `<img>` fails to load (broken URL, expired
 * presigned S3 link, CORS-blocked Google profile pic, etc.) we
 * fall back to the gradient + initials state instead of letting
 * the browser render raw alt text in a broken-image box. The
 * `alt` attribute is intentionally empty so a partial-load /
 * error transition never flashes the name as text — the parent
 * row already shows the fullName next to the avatar.
 *
 * The gradient mirrors the one the `UserMenu` (top-right of every
 * panel) has used since the blue-palette refresh, so every avatar
 * across the app now reads as the same visual family — a gradient
 * orb with either a face or two-letter initials inside.
 *
 * Sizes:
 *   xs · 24 px · 10 px text — for dense table rows or chips
 *   sm · 28 px · 11 px text — default row avatar (file owners, list members)
 *   md · 36 px · 13 px text — leaderboard cards, comment authors
 *   lg · 48 px · 16 px text — page heroes, modal headers
 *   xl · 64 px · 20 px text — profile page
 *
 * If you want a different gradient palette (e.g. for a specific
 * surface), pass `gradient="…"` with any valid Tailwind gradient
 * utility string. Defaults match the brand UserMenu chip.
 */
/**
 * 8-colour palette for hash-based avatars. Each user gets a stable
 * colour derived from their name (same input → same colour), so
 * multi-assignee cells read as a varied stack instead of a wall of
 * identical brand-gradient circles. The hues are picked so:
 *   1. They sit comfortably on both light + dark surface backgrounds
 *   2. White initials hit ~4.5:1 contrast on every swatch
 *   3. Adjacent colours are visually distinct in a stack of 3-4
 */
const AVATAR_HASH_PALETTE = [
  // Cyan + light-blue entries replaced 2026-05-22 with the sidebar's
  // navy palette so avatar circles no longer carry the bright cyan
  // accent that competes with the new navy brand chrome. The
  // remaining six hues stay vivid so multi-assignee stacks still
  // read as a varied row of orbs, not a wall of navy.
  '#02367B', // deep navy — was blue-400 (#60A5FA)
  '#34D399', // emerald-400
  '#F87171', // red-400
  '#FBBF24', // amber-400
  '#A78BFA', // violet-400
  '#F472B6', // pink-400
  '#0a2349', // mid navy — was cyan-400 (#22D3EE)
  '#FB923C', // orange-400
]

function avatarHashColor(name) {
  if (!name) return AVATAR_HASH_PALETTE[0]
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0
  }
  return AVATAR_HASH_PALETTE[Math.abs(hash) % AVATAR_HASH_PALETTE.length]
}

export function Avatar({
  name = '',
  avatarUrl = null,
  size = 'sm',
  className = '',
  // Default gradient retinted 2026-05-22 from ocean→cyan to a
  // brighter ocean → deep-navy ramp. Earlier attempt used both
  // sidebar-navy stops (#02367B → #0a2349) but that read as near-
  // black on the Profile page — the eye couldn't pick up the
  // gradient at all. The new ramp uses the brand Ocean (#006CA5)
  // on one end so the circle reads as confidently blue, while
  // keeping the deeper Navy on the other end for the same dimensional
  // depth the original cyan gradient had. No cyan in the mix.
  gradient = 'bg-gradient-to-br from-[#006CA5] to-[#02367B]',
  /** When true, the no-image initials circle uses a per-name hash
   *  colour instead of the brand gradient. Lets multi-assignee
   *  stacks read as varied (matches the Space List page). Existing
   *  callers default to the gradient so nothing else changes. */
  colorByName = false,
  /** When true (the DEFAULT), an interactive styled chip floats above the
   *  avatar on hover showing the full name — the same hover affordance the
   *  Space assignee stack uses, applied consistently across the whole app.
   *  Replaces the slow, unstyled browser-native `title` tooltip. Pass
   *  `tooltip={false}` to opt a specific avatar out (e.g. a decorative or
   *  self-evident one). No-ops automatically when the avatar has no `name`. */
  tooltip = true,
  /** No-photo fallback style: 'initials' (default — per-name letters) or
   *  'icon' (a filled person silhouette). Use 'icon' for the current
   *  user's OWN avatar placeholder (e.g. the My Profile picture preview);
   *  keep 'initials' for lists of different people so they stay
   *  distinguishable. */
  placeholder = 'initials',
}) {
  // Track whether the <img> failed to load — flip on `onError` and
  // render the initials fallback instead. Reset whenever the source
  // URL changes so a freshly-uploaded avatar gets a fresh chance.
  const [imgBroken, setImgBroken] = useState(false)
  useEffect(() => {
    setImgBroken(false)
  }, [avatarUrl])

  const dims = SIZES[size] ?? SIZES.sm
  const showImage = Boolean(avatarUrl) && !imgBroken
  // When showing initials AND the caller opted into hash colours,
  // skip the brand gradient class and inline the swatch colour. The
  // class still wins when the caller wants the original look.
  const useHashColor = !showImage && colorByName && Boolean(name)
  const swatch = useHashColor ? avatarHashColor(name) : null

  // Avoid the browser-native tooltip when the floating chip is on —
  // the OS tooltip would otherwise render on top of the custom one
  // and the user would see the name twice.
  const nativeTitle = tooltip ? undefined : name || undefined
  const bubble = (
    <span
      aria-hidden="true"
      className={
        `inline-flex items-center justify-center rounded-full shrink-0 overflow-hidden text-white font-semibold border border-transparent ` +
        `${dims.box} ${dims.text} ${showImage || useHashColor ? '' : gradient} ${className}`
      }
      style={useHashColor ? { backgroundColor: swatch } : undefined}
      title={nativeTitle}
    >
      {showImage ? (
        <img
          src={avatarUrl}
          alt=""
          // Explicit display dimensions help the browser pick the
          // optimal resampling path during downscale — without these
          // some browsers fall back to a softer bilinear filter.
          // Retina screens still get the full source resolution
          // because the underlying file is 512 px (resized client-
          // side before upload — see resizeImageDataUrl in
          // ProfilePage.jsx).
          width={dims.px}
          height={dims.px}
          // `decoding="async"` lets the browser decode off the main
          // thread, so a long list of file-owner avatars doesn't
          // janks on scroll.
          decoding="async"
          loading="lazy"
          // Google profile photos (lh3.googleusercontent.com) return 403
          // when the request carries a cross-origin referrer; no-referrer
          // makes them load. Harmless for uploaded S3 avatars.
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover"
          draggable={false}
          onError={() => setImgBroken(true)}
        />
      ) : placeholder === 'icon' ? (
        // Filled person silhouette — the no-photo placeholder for the
        // current user's own avatar (opted into per-call). Matches the
        // account-switcher avatar so every "my account" surface reads the
        // same. White (inherits the wrapper's text-white) on the gradient.
        <svg
          width={Math.round(dims.px * 0.66)}
          height={Math.round(dims.px * 0.66)}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      ) : (
        initialsOf(name)
      )}
    </span>
  )

  if (!tooltip || !name) return bubble
  return <AvatarWithTooltip name={name}>{bubble}</AvatarWithTooltip>
}

/**
 * Hover-tooltip wrapper for the Avatar bubble. Renders the chip via a
 * `createPortal(document.body, ...)` so no ancestor's `overflow:
 * hidden` / `overflow: auto` / sticky-cell clipping can truncate it
 * — the previous CSS-only `absolute bottom-full` version sat inside
 * the row's grid cell and got clipped by ancestor overflow on the
 * SpacePage list view (visible as a half-cut "Mayur Auti" chip on
 * the Assignee column).
 *
 * Position math:
 *   • Default: chip sits ABOVE the avatar with a downward-pointing
 *     caret, like the original CSS version.
 *   • If there isn't enough room above (the row sits near the top
 *     of the viewport — e.g. the first table row right under the
 *     header), the chip flips BELOW the avatar with the caret
 *     pointing up. Avoids "tooltip clipped by the browser chrome"
 *     which was the worst version of this bug.
 *   • Horizontal: centred over the avatar; clamped to the viewport
 *     so a corner avatar's chip never spills off the right edge.
 *
 * `pointer-events: none` on the chip itself so it never blocks
 * clicks on adjacent cells, same as the original implementation.
 */
function AvatarWithTooltip({ name, children }) {
  const wrapperRef = useRef(null)
  const [open, setOpen] = useState(false)
  // `coords` carries the absolute viewport pixel offsets the portal
  // chip anchors to, plus whether the chip flipped to the BELOW
  // position so the caret rotates accordingly.
  const [coords, setCoords] = useState(null)

  const measure = useCallback(() => {
    const el = wrapperRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    // Default position: ABOVE the avatar (the original UX the chip
    // pattern was designed for). The portal + fixed positioning
    // means no ancestor can clip the chip — the original
    // first-row-clipping concern is solved by the portal alone.
    //
    // Auto-flip BELOW only when the avatar sits within ~36 px of
    // the viewport top — the chip would otherwise spill above the
    // browser frame and the viewport itself would clip it. The 36 px
    // estimate covers chip line-height + py-1 + shadow.
    const ESTIMATED_H = 36
    const PADDING = 6
    const flipBelow = r.top < ESTIMATED_H + PADDING
    setCoords({
      // `top` is the chip's vertical anchor; CSS does the lift
      // (`translate(-50%, -100%)` for above, `translate(-50%, 0)`
      // for below).
      top: flipBelow ? r.bottom + PADDING : r.top - PADDING,
      // Horizontally centred over the avatar.
      left: r.left + r.width / 2,
      flipBelow,
    })
  }, [])

  // Recalculate on every show — scroll / window resize would shift
  // the anchor, so re-measure before the first paint of the chip.
  useLayoutEffect(() => {
    if (!open) return
    measure()
  }, [open, measure])

  // Hide on scroll anywhere (avoids a stale-position chip lingering
  // while the user scrolls the table). Listening with `capture: true`
  // catches nested scroll containers — the SpacePage list view scrolls
  // a wrapped div, not the page.
  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  return (
    <>
      <span
        ref={wrapperRef}
        className="inline-flex"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>
      {open && coords
        ? createPortal(
            <span
              role="tooltip"
              aria-label={name}
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                transform: coords.flipBelow
                  ? 'translate(-50%, 0)'
                  : 'translate(-50%, -100%)',
                zIndex: 1000,
              }}
              className="pointer-events-none whitespace-nowrap rounded-md bg-[color:var(--color-ink-strong)] text-[color:var(--color-canvas)] text-[12px] font-medium px-2 py-1 shadow-md"
            >
              {name}
              {/* Caret — points DOWN at the avatar when the chip
               *  sits above it (default), UP when flipped below
               *  (avatar near viewport top). */}
              <span
                aria-hidden="true"
                className={
                  coords.flipBelow
                    ? 'absolute bottom-full left-1/2 -translate-x-1/2 h-0 w-0 border-x-4 border-x-transparent border-b-4 border-b-[color:var(--color-ink-strong)]'
                    : 'absolute top-full left-1/2 -translate-x-1/2 h-0 w-0 border-x-4 border-x-transparent border-t-4 border-t-[color:var(--color-ink-strong)]'
                }
              />
            </span>,
            document.body,
          )
        : null}
    </>
  )
}

// `box` / `text` drive the Tailwind utility classes on the wrapper;
// `px` is the matching pixel dimension passed to the `<img>` element
// as explicit width/height attributes. The browser uses those to
// pick the right downscaling path during decode — without them the
// resampler is less aggressive about preserving edge sharpness,
// which was the "avatar looks blurry" complaint. Keep `px` in sync
// with the Tailwind class whenever a size tier is added.
const SIZES = {
  xs: { box: 'h-6 w-6', text: 'text-[10px]', px: 24 },
  sm: { box: 'h-7 w-7', text: 'text-[11px]', px: 28 },
  md: { box: 'h-9 w-9', text: 'text-[13px]', px: 36 },
  lg: { box: 'h-12 w-12', text: 'text-base', px: 48 },
  xl: { box: 'h-16 w-16', text: 'text-xl', px: 64 },
  // The Profile page's picture preview historically rendered at
  // 80px (h-20 w-20). Kept as a dedicated tier so other heroes
  // can opt into the same scale rather than every page re-deriving
  // a one-off size.
  '2xl': { box: 'h-20 w-20', text: 'text-2xl', px: 80 },
}

/** First + last initial, uppercase. Handles single-word names, blank
 *  input, and unicode by leaning on JS string-iteration. */
function initialsOf(name) {
  const trimmed = String(name ?? '').trim()
  if (trimmed.length === 0) return '?'
  const parts = trimmed.split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
